"use client";

import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function VoicesOfOurCommunity() {
  const testimonials = [{
    quote: "Hope Torchlighters has been a lifeline for our family. Their respite care program gave us the breathing room we desperately needed, and the inclusive events have helped our child feel truly seen and celebrated in our community.",
    authors: "THE MARTINEZ FAMILY",
    venue: "COMMUNITY OUTREACH",
    date: "AUGUST 2024",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/family_support_moment.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/family_support_moment.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/family_support_moment.png-wvc-sizes",
    alt: "A happy family participating in a community event"
  }, {
    quote: "The advocacy resources provided by Hope Torchlighters changed everything for us. They helped us navigate complex systems with confidence, and their bilingual staff made sure we understood every step of the process.",
    authors: "SARAH & DAVID",
    venue: "ADVOCACY SERVICES",
    date: "JULY 2024",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/advocacy_meeting.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/advocacy_meeting.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/advocacy_meeting.png-wvc-sizes",
    alt: "A parent receiving guidance from a staff member"
  }, {
    quote: "Finding a group that understands the unique needs of neurodiverse families is rare. Hope Torchlighters isn't just a service provider; they are a true neighborhood partner that fosters genuine connection and joy.",
    authors: "ELENA & MARCUS",
    venue: "INCLUSIVE PROGRAMS",
    date: "JUNE 2024",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/inclusive_play_group.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/inclusive_play_group.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/inclusive_play_group.png-wvc-sizes",
    alt: "Children playing together in an inclusive environment"
  }, {
    quote: "We feel so supported by the caregiver workshops. It’s comforting to know we aren't walking this path alone. Hope Torchlighters has built a vibrant, welcoming space where our whole family can thrive.",
    authors: "THE JOHNSONS",
    venue: "CAREGIVER SUPPORT",
    date: "MAY 2024",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/caregiver_workshop.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/caregiver_workshop.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/caregiver_workshop.png-wvc-sizes",
    alt: "Caregivers sharing experiences in a supportive workshop"
  }];
  const [activeIndex, setActiveIndex] = useState(0);
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % testimonials.length);
    }, 7000);
    return () => clearInterval(timer);
  }, [testimonials.length]);
  const goPrev = () => {
    setActiveIndex(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };
  const goNext = () => {
    setActiveIndex(prev => (prev + 1) % testimonials.length);
  };
  const currentCounter = String(activeIndex + 1).padStart(2, "0");
  const totalCounter = String(testimonials.length).padStart(2, "0");
  return <section data-section-id="572" id="voices-of-our-community" className="relative overflow-hidden bg-background py-20 md:py-32 text-foreground">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.06]">
        <svg className="absolute -right-20 top-10 h-[600px] w-[600px] text-primary" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M100 20 Q120 60 100 100 Q80 140 100 180" stroke="currentColor" strokeWidth="0.5" />
          <path d="M100 40 Q140 50 150 80" stroke="currentColor" strokeWidth="0.5" />
          <path d="M100 60 Q60 70 50 100" stroke="currentColor" strokeWidth="0.5" />
          <path d="M100 90 Q150 100 160 130" stroke="currentColor" strokeWidth="0.5" />
          <path d="M100 110 Q50 120 40 150" stroke="currentColor" strokeWidth="0.5" />
          <circle cx="100" cy="40" r="3" fill="currentColor" />
          <circle cx="150" cy="80" r="3" fill="currentColor" />
          <circle cx="50" cy="100" r="3" fill="currentColor" />
          <circle cx="160" cy="130" r="3" fill="currentColor" />
          <circle cx="40" cy="150" r="3" fill="currentColor" />
        </svg>
        <svg className="absolute -left-20 bottom-10 h-[500px] w-[500px] text-secondary" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M30 100 Q70 80 110 100 Q150 120 190 100" stroke="currentColor" strokeWidth="0.5" />
          <path d="M50 90 Q60 70 80 60" stroke="currentColor" strokeWidth="0.5" />
          <path d="M130 110 Q140 130 160 140" stroke="currentColor" strokeWidth="0.5" />
          <circle cx="80" cy="60" r="3" fill="currentColor" />
          <circle cx="160" cy="140" r="3" fill="currentColor" />
        </svg>
      </div>

      <div ref={ref} className={`relative mx-auto max-w-7xl px-6 transition-all duration-1000 lg:px-12 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
        {}
        <div className="mb-16 text-center md:mb-24">
          <p className="font-default text-xs uppercase tracking-[0.3em] text-secondary font-bold">
            Community Impact
          </p>
          <h2 className="mt-6 font-default text-4xl font-semibold leading-tight text-foreground md:text-6xl lg:text-7xl">
            <span className="italic text-primary">Voices</span>{" "}
            <span className="font-light">of Our Community</span>
          </h2>
        </div>

        {}
        <div className="relative">
          {}
          <Button variant="ghost" size="icon" onClick={goPrev} aria-label="Previous testimonial" className="absolute -left-2 top-1/2 z-20 hidden -translate-y-1/2 text-foreground hover:bg-transparent hover:text-primary md:flex lg:-left-8">
            <ChevronLeft className="size-8" strokeWidth={1} />
          </Button>
          <Button variant="ghost" size="icon" onClick={goNext} aria-label="Next testimonial" className="absolute -right-2 top-1/2 z-20 hidden -translate-y-1/2 text-foreground hover:bg-transparent hover:text-primary md:flex lg:-right-8">
            <ChevronRight className="size-8" strokeWidth={1} />
          </Button>

          <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-12 lg:gap-20">
            {}
            <div className="relative md:col-span-5">
              <div className="relative aspect-[3/4] w-full overflow-hidden md:aspect-[4/5]">
                {testimonials.map((t, i) => <img key={i} data-index={i} src={t.image} data-wvc-srcset={t.srcset} data-wvc-sizes={t.sizes} alt={t.alt} loading="lazy" decoding="async" className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ease-in-out ${activeIndex === i ? "opacity-100" : "opacity-0"}`} />)}
              </div>
            </div>

            {}
            <div className="relative flex flex-col justify-center md:col-span-7">
              {}
              <span aria-hidden="true" className="pointer-events-none absolute -top-8 -left-2 select-none font-default text-[120px] leading-none text-secondary/20 md:-top-12 md:text-[160px]">
                &ldquo;
              </span>

              <div className="relative min-h-[280px] md:min-h-[320px]">
                {testimonials.map((t, i) => <div key={i} data-index={i} className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${activeIndex === i ? "opacity-100" : "pointer-events-none opacity-0"}`}>
                    <p className="relative z-10 pt-8 font-default text-xl font-normal italic leading-relaxed text-foreground md:text-2xl md:leading-[1.7]">
                      {t.quote}
                    </p>

                    <div className="mt-10 h-px w-24 bg-primary/20" />

                    <p className="mt-6 font-default text-xs uppercase tracking-[0.25em] text-muted-foreground font-bold">
                      <span className="text-foreground">{t.authors}</span>
                      <span className="mx-2 text-secondary">&middot;</span>
                      <span>{t.venue}</span>
                      <span className="mx-2 text-secondary">&middot;</span>
                      <span>{t.date}</span>
                    </p>
                  </div>)}
              </div>

              {}
              <div className="mt-8 flex items-end justify-between md:mt-12">
                {}
                <div className="flex items-center gap-3">
                  {testimonials.map((_, i) => <Button key={i} data-index={i} type="button" variant="ghost" onClick={() => setActiveIndex(i)} aria-label={`Go to testimonial ${i + 1}`} className={`h-1.5 rounded-md p-0 transition-all duration-500 hover:bg-transparent ${activeIndex === i ? "w-8 bg-secondary" : "w-1.5 bg-primary/20 hover:bg-secondary/60"}`} />)}
                </div>

                <p className="font-default text-lg italic text-muted-foreground">
                  <span className="text-foreground">{currentCounter}</span>
                  <span className="mx-1 text-secondary">/</span>
                  <span>{totalCounter}</span>
                </p>
              </div>

              {}
              <div className="mt-8 flex items-center justify-center gap-4 md:hidden">
                <Button variant="ghost" size="icon" onClick={goPrev} aria-label="Previous testimonial" className="text-foreground hover:bg-transparent hover:text-primary">
                  <ChevronLeft className="size-6" strokeWidth={1} />
                </Button>
                <Button variant="ghost" size="icon" onClick={goNext} aria-label="Next testimonial" className="text-foreground hover:bg-transparent hover:text-primary">
                  <ChevronRight className="size-6" strokeWidth={1} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}